/**
 * Turn an axios error into a string that is always safe to render.
 *
 * FastAPI sends `detail` as a plain string for HTTPException, but as an array
 * of `{loc, msg, type}` objects for 422 request-validation errors. Passing that
 * array (or any object) straight into JSX throws React error #31 ("Objects are
 * not valid as a React child"), which unmounts the tree and leaves a blank
 * page. Never render `detail` unfiltered — route it through here.
 *
 * Returns `fallback` when there is no usable message, so the caller always has
 * something to show.
 */
export function errorMessage(err, fallback = "") {
  return detailToText(err?.response?.data?.detail) || fallback;
}

/**
 * Stable machine-readable code from an API error, e.g. "ai_quota_exceeded".
 *
 * Endpoints that answer with `{code, message}` intend the code to drive the UI:
 * the message is a generic English fallback, never internal error text. Prefer
 * translating the code over rendering the message.
 */
export function errorCode(err) {
  const detail = err?.response?.data?.detail;
  const code = detail && typeof detail === "object" && !Array.isArray(detail) ? detail.code : null;
  return typeof code === "string" ? code : "";
}

// Coded refusals from a gate, and the string that says each one properly.
// `email_not_verified` came first (posting a job, sending a quote, starting a
// checkout); DY2.6 adds the two that guard who may own and who may quote a job.
// `cannot_quote_own_job` in particular has to read as a rule and not as a
// failure — the person did nothing wrong, the marketplace simply does not work
// that way.
// The [FIX] of 2026-09-07 adds four more, all about a job posted for one named
// FixyBee: three the job form can hit when the FixyBee it names does not check
// out, and one the quote form hits when the job belongs to somebody else's
// invitation. Same principle as `cannot_quote_own_job` — a rule, not a failure.
const GATE_CODES = {
  email_not_verified: "auth.verifyBanner.blocked",
  cannot_quote_own_job: "sendQuoteX.cannotQuoteOwn",
  role_required: "postJobX.roleRequired",
  job_is_directed: "sendQuoteX.jobIsDirected",
  directed_provider_not_found: "postJobX.directedNotFound",
  directed_target_not_provider: "postJobX.directedNotProvider",
  cannot_direct_to_self: "postJobX.directedToSelf",
  directed_category_not_served: "postJobX.directedCategoryNotServed",
  directed_subcategory_not_served: "postJobX.directedSubcategoryNotServed",
  // 2026-10-28: the one phone gate left — a 100% courtesy code.
  phone_verification_required: "signupX.gateCourtesy",
};

/**
 * Localized text for an action refused by a gate that carries a code.
 *
 * The standing banner is the CTA — this just stops the inline message being the
 * server's English fallback.
 */
export function gateErrorText(err, t, fallback = "") {
  const key = GATE_CODES[errorCode(err)];
  if (key) return t(key);
  return errorMessage(err, fallback);
}

const AI_ERROR_CODES = ["ai_disabled", "ai_budget_exhausted", "ai_quota_exceeded", "ai_error"];

/**
 * Localized text for an error from an /ai/* endpoint.
 *
 * Those endpoints answer with a stable code and a deliberately generic English
 * message (internal error text stays server-side), so the code is what carries
 * meaning. Anything that is not a known AI code — a 403, a validation error —
 * still renders through `errorMessage`, which keeps its own useful detail.
 */
export function aiErrorText(err, t, fallback = "") {
  const code = errorCode(err);
  return AI_ERROR_CODES.includes(code)
    ? t(`common.aiErrors.${code}`, { defaultValue: fallback })
    : errorMessage(err, fallback);
}

function detailToText(detail) {
  if (detail === null || detail === undefined) return "";
  if (typeof detail === "string") return detail.trim();
  if (Array.isArray(detail)) {
    // One line per invalid field, each sentence-terminated so they read as prose.
    return detail
      .map(itemToText)
      .filter(Boolean)
      .map((m) => (/[.!?]$/.test(m) ? m : `${m}.`))
      .join(" ");
  }
  if (typeof detail === "object") return itemToText(detail);
  return String(detail);
}

function itemToText(item) {
  if (item === null || item === undefined) return "";
  if (typeof item === "string") return item.trim();
  if (typeof item !== "object") return String(item);

  const raw = typeof item.msg === "string" ? item.msg : item.message;
  const msg = typeof raw === "string" ? raw.trim() : "";
  if (!msg) return "";

  const field = fieldFromLoc(item.loc);
  return field ? `${field}: ${msg}` : msg;
}

// FastAPI's `loc` looks like ["body", "email"]; keep only the field name.
function fieldFromLoc(loc) {
  if (!Array.isArray(loc)) return "";
  const parts = loc.filter(
    (p) => typeof p === "string" && !["body", "query", "path", "header", "cookie"].includes(p)
  );
  return parts.length ? parts[parts.length - 1] : "";
}

export default errorMessage;
